import React, { useState, useEffect } from 'react';
import api from '../api';
import { todayLocalStr, firstOfMonthLocalStr } from '../utils/dateUtils';

export default function PLSheet() {
  const [data, setData] = useState([]);
  const [startDate, setStartDate] = useState(firstOfMonthLocalStr());
  const [endDate, setEndDate] = useState(todayLocalStr());
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchPL(); }, []);

  const fetchPL = async () => {
    setLoading(true);
    try {
      const res = await api.get('/pl-sheet?start=' + startDate + '&end=' + endDate);
      setData(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fmt = (n) => '₹' + (n || 0).toLocaleString('en-IN');

  const totals = data.reduce((acc, row) => ({
    collection: acc.collection + row.collection,
    disbursement: acc.disbursement + row.disbursement,
    income: acc.income + row.income,
    capitalRepay: acc.capitalRepay + row.capital_repayment,
    givenAmount: acc.givenAmount + row.given_amount,
    deductedAmount: acc.deductedAmount + row.deducted_amount,
    expense: acc.expense + row.expense,
    writtenOff: acc.writtenOff + (row.written_off || 0)
  }), { collection: 0, disbursement: 0, income: 0, capitalRepay: 0, givenAmount: 0, deductedAmount: 0, expense: 0, writtenOff: 0 });

  // Written-off capital (Mark Off) is a real, permanent loss of principal,
  // so it comes out of Net Profit alongside expenses -- at your request,
  // waived interest is never part of this since it was never counted as
  // income in the first place.
  const netProfit = totals.income - totals.expense - totals.writtenOff;
  const lastRow = data[data.length - 1] || {};

  return (
    <div>
      <h2 style={{ fontSize: 24, fontWeight: 'bold', marginBottom: 16 }}>Profit & Loss Sheet</h2>

      <div style={{ background: 'white', borderRadius: 8, padding: 12, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end' }}>
        <div>
          <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 2 }}>Start Date</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6 }} />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 2 }}>End Date</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6 }} />
        </div>
        <button onClick={fetchPL} style={{ background: '#2563eb', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' }}>Apply</button>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40, fontSize: 16, color: '#6b7280' }}>Loading...</div>}

      {!loading && (
        <>
          {/* Profit Summary */}
          <div style={{ background: 'white', borderRadius: 12, padding: 20, marginBottom: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderLeft: '6px solid ' + (netProfit >= 0 ? '#10b981' : '#ef4444') }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
              <div>
                <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Total Income (Interest)</p>
                <p style={{ fontSize: 22, fontWeight: 'bold', color: '#16a34a', margin: '4px 0 0 0' }}>{fmt(totals.income)}</p>
              </div>
              <div>
                <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Total Expenses</p>
                <p style={{ fontSize: 22, fontWeight: 'bold', color: '#dc2626', margin: '4px 0 0 0' }}>{fmt(totals.expense)}</p>
              </div>
              <div>
                <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Capital Repaid</p>
                <p style={{ fontSize: 22, fontWeight: 'bold', color: '#7c3aed', margin: '4px 0 0 0' }}>{fmt(totals.capitalRepay)}</p>
              </div>
              <div>
                <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Capital Written Off</p>
                <p style={{ fontSize: 22, fontWeight: 'bold', color: '#b91c1c', margin: '4px 0 0 0' }}>{fmt(totals.writtenOff)}</p>
              </div>
              <div style={{ background: netProfit >= 0 ? '#f0fdf4' : '#fef2f2', borderRadius: 8, padding: 10 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: netProfit >= 0 ? '#16a34a' : '#dc2626', margin: 0 }}>
                  {netProfit >= 0 ? '📈 NET PROFIT' : '📉 NET LOSS'}
                </p>
                <p style={{ fontSize: 24, fontWeight: 'bold', color: netProfit >= 0 ? '#16a34a' : '#dc2626', margin: '4px 0 0 0' }}>{fmt(Math.abs(netProfit))}</p>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8, marginBottom: 16 }}>
            {[
              { label: 'Collection', value: totals.collection, color: '#10b981' },
              { label: 'Disbursement', value: totals.disbursement, color: '#3b82f6' },
              { label: 'Given Amount', value: totals.givenAmount, color: '#0284c7' },
              { label: 'Total Deducted Amount', value: totals.deductedAmount, color: '#ea580c' },
              { label: 'Income', value: totals.income, color: '#6366f1' },
              { label: 'Capital Repay', value: totals.capitalRepay, color: '#7c3aed' },
              { label: 'Written Off', value: totals.writtenOff, color: '#b91c1c' },
              { label: 'Expenses', value: totals.expense, color: '#ef4444' },
              { label: 'Cash EOD', value: lastRow.cash_eod, color: '#0891b2' },
              { label: 'Bank EOD', value: lastRow.bank_eod, color: '#1e40af' },
              { label: 'Capital in Market', value: lastRow.capital_in_market, color: '#7c3aed' },
              { label: 'Monthly Expected', value: lastRow.monthly_income, color: '#f59e0b' },
            ].map((card, i) => (
              <div key={i} style={{ background: card.color, borderRadius: 8, padding: 10, color: 'white' }}>
                <p style={{ fontSize: 10, opacity: 0.9, margin: 0 }}>{card.label}</p>
                <p style={{ fontSize: 16, fontWeight: 'bold', margin: '2px 0 0 0' }}>{fmt(card.value)}</p>
              </div>
            ))}
          </div>

          {/* Day-wise Table */}
          {data.length > 0 && (
            <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: '#6b7280' }}>Date</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, fontWeight: 500, color: '#6b7280' }}>Collection</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, fontWeight: 500, color: '#6b7280' }}>Disbursement</th><th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, fontWeight: 500, color: '#6b7280' }}>Given</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, fontWeight: 500, color: '#6b7280' }}>Deducted</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, fontWeight: 500, color: '#6b7280' }}>Income</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, fontWeight: 500, color: '#6b7280' }}>Capital Repay</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, fontWeight: 500, color: '#6b7280' }}>Expense</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, fontWeight: 500, color: '#6b7280' }}>Written Off</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, fontWeight: 500, color: '#6b7280' }}>Cash EOD</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, fontWeight: 500, color: '#6b7280' }}>Bank EOD</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, fontWeight: 500, color: '#6b7280' }}>Market Capital</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, fontWeight: 500, color: '#6b7280' }}>Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => {
                    const profit = row.income - row.expense - (row.written_off || 0);
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '6px 10px', fontWeight: 500 }}>{new Date(row.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right' }}>{row.collection > 0 ? fmt(row.collection) : '-'}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right', color: row.disbursement > 0 ? '#dc2626' : '#6b7280' }}>{row.disbursement > 0 ? fmt(row.disbursement) : '-'}</td><td style={{ padding: '6px 10px', textAlign: 'right', color: row.given_amount > 0 ? '#0284c7' : '#6b7280' }}>{row.given_amount > 0 ? fmt(row.given_amount) : '-'}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right', color: row.deducted_amount > 0 ? '#ea580c' : '#6b7280' }}>{row.deducted_amount > 0 ? fmt(row.deducted_amount) : '-'}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right', color: row.income > 0 ? '#16a34a' : '#6b7280' }}>{row.income > 0 ? fmt(row.income) : '-'}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right', color: row.capital_repayment > 0 ? '#7c3aed' : '#6b7280' }}>{row.capital_repayment > 0 ? fmt(row.capital_repayment) : '-'}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right', color: row.expense > 0 ? '#dc2626' : '#6b7280' }}>{row.expense > 0 ? fmt(row.expense) : '-'}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right', color: row.written_off > 0 ? '#b91c1c' : '#6b7280' }}>{row.written_off > 0 ? fmt(row.written_off) : '-'}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 500 }}>{fmt(row.cash_eod)}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 500 }}>{fmt(row.bank_eod)}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 500 }}>{fmt(row.capital_in_market)}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: profit >= 0 ? '#16a34a' : '#dc2626' }}>{fmt(profit)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#f0f9ff', fontWeight: 700, borderTop: '2px solid #2563eb' }}>
                    <td style={{ padding: '10px 10px' }}>TOTAL</td>
                    <td style={{ padding: '10px 10px', textAlign: 'right', color: '#16a34a' }}>{fmt(totals.collection)}</td>
                    <td style={{ padding: '10px 10px', textAlign: 'right', color: '#dc2626' }}>{fmt(totals.disbursement)}</td><td style={{ padding: '10px 10px', textAlign: 'right', color: '#0284c7' }}>{fmt(totals.givenAmount)}</td>
                    <td style={{ padding: '10px 10px', textAlign: 'right', color: '#ea580c' }}>{fmt(totals.deductedAmount)}</td>
                    <td style={{ padding: '10px 10px', textAlign: 'right', color: '#16a34a' }}>{fmt(totals.income)}</td>
                    <td style={{ padding: '10px 10px', textAlign: 'right', color: '#7c3aed' }}>{fmt(totals.capitalRepay)}</td>
                    <td style={{ padding: '10px 10px', textAlign: 'right', color: '#dc2626' }}>{fmt(totals.expense)}</td>
                    <td style={{ padding: '10px 10px', textAlign: 'right', color: '#b91c1c' }}>{fmt(totals.writtenOff)}</td>
                    <td style={{ padding: '10px 10px', textAlign: 'right' }}></td>
                    <td style={{ padding: '10px 10px', textAlign: 'right' }}></td>
                    <td style={{ padding: '10px 10px', textAlign: 'right' }}></td>
                    <td style={{ padding: '10px 10px', textAlign: 'right', color: netProfit >= 0 ? '#16a34a' : '#dc2626' }}>{fmt(netProfit)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

